import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { formatCurrency as baseFormatCurrency } from '../utils/formatters';

const PreferencesContext = createContext();

export const usePreferences = () => {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
};

// Categories that can be excluded from revenue split calculations
// Shared between CoHostDetail and CoHostManagement for consistency
export const SPLIT_EXCLUSION_OPTIONS = [
  // Pass-through reimbursements (commonly excluded)
  { value: 'tolls', label: 'Tolls', group: 'Reimbursements', description: 'Toll charges passed through to guest' },
  { value: 'gas_reimbursement', label: 'Gas Reimbursements', group: 'Reimbursements', description: 'Fuel costs reimbursed by guest' },
  { value: 'ev_charging', label: 'EV Charging', group: 'Reimbursements', description: 'Electric vehicle charging costs' },
  // Additional fees
  { value: 'late_fee', label: 'Late Fees', group: 'Fees', description: 'Charges for late returns' },
  { value: 'cancellation_fee', label: 'Cancellation Fees', group: 'Fees', description: 'Fees from cancelled bookings' },
  { value: 'cleaning_fee', label: 'Cleaning Fees', group: 'Fees', description: 'Vehicle cleaning charges' },
  { value: 'delivery_fee', label: 'Delivery Fees', group: 'Fees', description: 'Vehicle delivery/pickup fees' },
  // Other revenue streams
  { value: 'extras', label: 'Extras / Add-ons', group: 'Revenue', description: 'Optional add-on purchases' },
  { value: 'additional_usage', label: 'Additional Usage', group: 'Revenue', description: 'Extra mileage or usage charges' },
  { value: 'boost', label: 'Boost Earnings', group: 'Revenue', description: 'Promotional boost earnings' },
];

// Group exclusion options by category for better UI display
export const SPLIT_EXCLUSION_GROUPS = [
  { 
    name: 'Reimbursements', 
    description: 'Pass-through costs typically kept by host',
    options: SPLIT_EXCLUSION_OPTIONS.filter(o => o.group === 'Reimbursements')
  },
  { 
    name: 'Fees', 
    description: 'Additional charges from trips',
    options: SPLIT_EXCLUSION_OPTIONS.filter(o => o.group === 'Fees')
  },
  { 
    name: 'Revenue', 
    description: 'Extra income streams',
    options: SPLIT_EXCLUSION_OPTIONS.filter(o => o.group === 'Revenue')
  },
];

// Top 5 world currencies
export const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
];

// Date format options
export const DATE_FORMATS = [
  { id: 'MM/DD/YYYY', label: 'MM/DD/YYYY (US)', example: '12/31/2024' },
  { id: 'DD/MM/YYYY', label: 'DD/MM/YYYY (EU)', example: '31/12/2024' },
  { id: 'YYYY-MM-DD', label: 'YYYY-MM-DD (ISO)', example: '2024-12-31' },
];

// Common timezones
export const TIMEZONES = [
  { id: 'America/New_York', label: 'Eastern Time (ET)', offset: 'UTC-5' },
  { id: 'America/Chicago', label: 'Central Time (CT)', offset: 'UTC-6' },
  { id: 'America/Denver', label: 'Mountain Time (MT)', offset: 'UTC-7' },
  { id: 'America/Los_Angeles', label: 'Pacific Time (PT)', offset: 'UTC-8' },
  { id: 'America/Anchorage', label: 'Alaska Time (AKT)', offset: 'UTC-9' },
  { id: 'Pacific/Honolulu', label: 'Hawaii Time (HT)', offset: 'UTC-10' },
  { id: 'Europe/London', label: 'London (GMT/BST)', offset: 'UTC+0' },
  { id: 'Europe/Paris', label: 'Paris (CET)', offset: 'UTC+1' },
  { id: 'Asia/Tokyo', label: 'Tokyo (JST)', offset: 'UTC+9' },
  { id: 'Asia/Shanghai', label: 'Shanghai (CST)', offset: 'UTC+8' },
  { id: 'Australia/Sydney', label: 'Sydney (AEDT)', offset: 'UTC+11' },
];

export const PreferencesProvider = ({ children }) => {
  const [preferences, setPreferences] = useState({
    currency: 'USD',
    dateFormat: 'MM/DD/YYYY',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    excludeFromSplit: [], // Categories to exclude from cohost revenue split
  });

  // Load preferences from localStorage on mount (account-scoped)
  useEffect(() => {
    const accountId = localStorage.getItem('activeAccountId');
    const key = accountId ? `userPreferences_${accountId}` : 'userPreferences';
    let saved = localStorage.getItem(key);
    // Migrate from unscoped key if account-scoped key doesn't exist
    if (!saved && key !== 'userPreferences') {
      saved = localStorage.getItem('userPreferences');
    }
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setPreferences(prev => ({ ...prev, ...parsed }));
      } catch (error) {
        console.error('Failed to parse preferences:', error);
      }
    }
  }, []);

  // Save preferences to localStorage whenever they change (account-scoped)
  useEffect(() => {
    const accountId = localStorage.getItem('activeAccountId');
    const key = accountId ? `userPreferences_${accountId}` : 'userPreferences';
    localStorage.setItem(key, JSON.stringify(preferences));
  }, [preferences]);

  const updatePreference = useCallback((key, value) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
  }, []);

  const formatCurrency = (amount, currencyCode = preferences.currency) =>
    baseFormatCurrency(amount, {
      currency: currencyCode,
      locale: 'en-US',
      showCents: !(currencyCode === 'JPY' || currencyCode === 'CNY')
    });

  const formatDate = (date, formatId = preferences.dateFormat) => {
    if (!date) return '';
    
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    
    switch (formatId) {
      case 'MM/DD/YYYY':
        return `${month}/${day}/${year}`;
      case 'DD/MM/YYYY':
        return `${day}/${month}/${year}`;
      case 'YYYY-MM-DD':
        return `${year}-${month}-${day}`;
      default:
        return `${month}/${day}/${year}`;
    }
  };

  const formatDateTime = (date, timezone = preferences.timezone) => {
    if (!date) return '';
    
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    
    try {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(d);
    } catch (error) {
      console.error('Error formatting date with timezone:', error);
      return d.toLocaleString();
    }
  };

  // Toggle a category in the exclusion list
  const toggleExclusion = (category) => {
    setPreferences(prev => {
      const currentExclusions = prev.excludeFromSplit || [];
      const newExclusions = currentExclusions.includes(category)
        ? currentExclusions.filter(c => c !== category)
        : [...currentExclusions, category];
      return { ...prev, excludeFromSplit: newExclusions };
    });
  };

  // Set all exclusions at once
  const setExclusions = (exclusions) => {
    setPreferences(prev => ({ ...prev, excludeFromSplit: exclusions || [] }));
  };

  // Clear all exclusions
  const clearExclusions = () => {
    setPreferences(prev => ({ ...prev, excludeFromSplit: [] }));
  };

  const value = {
    preferences,
    updatePreference,
    formatCurrency,
    formatDate,
    formatDateTime,
    currencies: CURRENCIES,
    dateFormats: DATE_FORMATS,
    timezones: TIMEZONES,
    // Exclusion helpers
    splitExclusionOptions: SPLIT_EXCLUSION_OPTIONS,
    splitExclusionGroups: SPLIT_EXCLUSION_GROUPS,
    toggleExclusion,
    setExclusions,
    clearExclusions,
  };

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  );
};
